import { bnPageHead, BN_TITLE_SUFFIX } from "@/lib/bn-head";
import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { JsonLd } from "@/components/JsonLd";
import { BN_POSTS, type BnPostMeta } from "@/content/bn/posts";
import { BN_CATEGORY_KEYWORDS } from "@/content/bn/keywords";
import { SITE } from "@/content/site";
import { isPairedCategory } from "@/content/language-pairs";
import { BN_CATEGORY_SLUGS } from "@/content/bn/categories";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

type CategoryCopy = { tagline: string; intro: string; keywords: string };

const BN_CATEGORY_COPY: Record<string, CategoryCopy> = {
  aeo: {
    tagline: "এআই সার্চ (AEO)",
    intro:
      "ChatGPT, Perplexity, Gemini ও Google AI Overviews-এ আপনার ব্র্যান্ড কীভাবে উদ্ধৃত হবে — বাংলায় AEO ও GEO-র প্র্যাকটিকাল গাইড।",
    keywords: "aeo bangla, ai search bangla, chatgpt seo bangla, generative engine optimization",
  },
  "digital-marketing": {
    tagline: "ডিজিটাল মার্কেটিং",
    intro:
      "বাংলাদেশের প্রেক্ষাপটে ডিজিটাল মার্কেটিং কৌশল, রোডম্যাপ এবং ক্যারিয়ার গাইড — শূন্য থেকে শুরু করে অ্যাডভান্সড পর্যন্ত প্র্যাকটিকাল বাংলা গাইড।",
    keywords:
      "digital marketing bangla, ডিজিটাল মার্কেটিং বাংলা, digital marketing bangladesh",
  },
  seo: {
    tagline: "এসইও (SEO)",
    intro:
      "Google-এ র‍্যাঙ্ক করার জন্য সম্পূর্ণ বাংলা SEO টিউটোরিয়াল — কীওয়ার্ড রিসার্চ, on-page, technical SEO, link building এবং AEO/GEO।",
    keywords: "seo bangla, এসইও বাংলা, seo tutorial bangladesh",
  },
  "social-media": {
    tagline: "সোশ্যাল মিডিয়া",
    intro:
      "ফেসবুক, ইনস্টাগ্রাম, ইউটিউব এবং টিকটকে অর্গানিক ও পেইড গ্রোথ — বাংলাদেশের মার্কেটের জন্য কনটেন্ট, রিলস এবং কমিউনিটি গাইড।",
    keywords:
      "facebook marketing bangla, social media marketing bangladesh",
  },
  freelancing: {
    tagline: "ফ্রিল্যান্সিং",
    intro:
      "আন্তর্জাতিক ক্লায়েন্ট পাওয়া, রেট সেট করা, Payoneer/Wise সেটআপ এবং দীর্ঘমেয়াদী ফ্রিল্যান্স ক্যারিয়ার গড়ার সম্পূর্ণ বাংলা গাইড।",
    keywords:
      "freelancing bangla, ফ্রিল্যান্সিং বাংলাদেশ, digital marketing freelancing",
  },
  "paid-ads": {
    tagline: "পেইড অ্যাডস",
    intro:
      "Google Ads, Facebook Ads এবং YouTube Ads — ক্যাম্পেইন স্ট্রাকচার, Quality Score, কনভার্সন ট্র্যাকিং এবং ROAS অপটিমাইজেশনের বাংলা গাইড।",
    keywords: "google ads bangla, facebook ads bangladesh, ppc bangla",
  },
  "content-marketing": {
    tagline: "কন্টেন্ট মার্কেটিং",
    intro:
      "ব্লগ, ভিডিও, পডকাস্ট ও নিউজলেটারের মাধ্যমে দীর্ঘমেয়াদী ROI গড়ার কৌশল — কন্টেন্ট স্ট্র্যাটেজি ও রিপারপাজিং ওয়ার্কফ্লো।",
    keywords: "content marketing bangla, কন্টেন্ট মার্কেটিং",
  },
  "email-marketing": {
    tagline: "ইমেইল মার্কেটিং",
    intro:
      "লিস্ট বিল্ডিং, অটোমেশন (Welcome, Abandoned Cart), সেগমেন্টেশন এবং ডেলিভারেবিলিটি — ইমেইল মার্কেটিংয়ের পূর্ণাঙ্গ বাংলা গাইড।",
    keywords: "email marketing bangla, ইমেইল মার্কেটিং বাংলাদেশ",
  },
  ecommerce: {
    tagline: "ই-কমার্স মার্কেটিং",
    intro:
      "Shopify, Daraz এবং F-commerce — বাংলাদেশের ই-কমার্স ব্যবসার জন্য মাল্টি-চ্যানেল মার্কেটিং ও কনভার্সন অপটিমাইজেশন গাইড।",
    keywords: "ecommerce marketing bangladesh, f-commerce, daraz marketing",
  },
  "local-seo": {
    tagline: "লোকাল SEO",
    intro:
      "Google Business Profile, Map Pack এবং লোকাল সিটেশন — বাংলাদেশের লোকাল ব্যবসার জন্য সম্পূর্ণ লোকাল SEO গাইড।",
    keywords: "local seo bangla, google business profile bangladesh",
  },
};

function postsInCategory(name: string): BnPostMeta[] {
  return BN_POSTS.filter((p) => p.category === name).sort((a, b) =>
    a.date < b.date ? 1 : -1,
  );
}

export const Route = createFileRoute("/bn/blog/category/$category")({
  loader: ({ params }) => {
    const slug = params.category.toLowerCase();
    const name = BN_CATEGORY_SLUGS[slug];
    if (!name) throw notFound();
    const posts = postsInCategory(name);
    return { slug, name, count: posts.length };
  },
  head: ({ loaderData, params }) => {
    const slug = params.category.toLowerCase();
    const name = loaderData?.name ?? BN_CATEGORY_SLUGS[slug];
    if (!name) {
      return {
        meta: [
          { title: "ক্যাটাগরি পাওয়া যায়নি" },
          { name: "robots", content: "noindex, nofollow" },
        ],
      };
    }
    const copy = BN_CATEGORY_COPY[slug];
    return bnPageHead({
      path: `/bn/blog/category/${slug}`,
      enPath: isPairedCategory(slug) ? `/blog/category/${slug}` : undefined,
      title: `${name} আর্টিকেল — বাংলা ব্লগ${BN_TITLE_SUFFIX}`,
      desc:
        copy?.intro.slice(0, 158) ??
        `${name} বিষয়ে বাংলায় গভীর গাইড — বাংলাদেশের প্রেক্ষাপটে প্র্যাকটিকাল আর্টিকেল।`,
      keywords: [copy?.keywords, BN_CATEGORY_KEYWORDS[name]],
      suffix: false,
      twitterCard: false,
    });
  },
  notFoundComponent: () => (
    <Section className="py-24">
      <h1 className="font-display text-3xl">ক্যাটাগরি পাওয়া যায়নি</h1>
      <Link
        to="/bn/blog"
        className="mt-4 inline-flex items-center gap-1 text-primary"
      >
        <ArrowLeft className="h-4 w-4" /> সব আর্টিকেলে ফিরে যান
      </Link>
    </Section>
  ),
  component: BnCategoryPage,
});

function BnCategoryPage() {
  const { slug, name } = Route.useLoaderData();
  const posts = postsInCategory(name);
  const copy = BN_CATEGORY_COPY[slug];
  const url = `${SITE.url}/bn/blog/category/${slug}`;

  const otherCategories = Object.entries(BN_CATEGORY_SLUGS)
    .filter(([s]) => s !== slug)
    .map(([s, n]) => ({ slug: s, name: n, count: postsInCategory(n).length }))
    .filter((c) => c.count > 0)
    .sort((a, b) => b.count - a.count);

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    inLanguage: "bn-BD",
    name: `${name} — বাংলা ব্লগ`,
    description: copy?.intro ?? "",
    url,
    isPartOf: {
      "@type": "Blog",
      name: "বাংলা ব্লগ — ফ্রিল্যান্সার তামাল",
      url: `${SITE.url}/bn/blog`,
    },
    about: { "@type": "Thing", name },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: posts.length,
      itemListElement: posts.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE.url}/bn/blog/${p.slug}`,
        name: p.title,
      })),
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "হোম", item: `${SITE.url}/bn` },
      { "@type": "ListItem", position: 2, name: "ব্লগ", item: `${SITE.url}/bn/blog` },
      { "@type": "ListItem", position: 3, name, item: url },
    ],
  };

  return (
    <>
      <JsonLd data={collectionSchema} />
      <JsonLd data={breadcrumbSchema} />

      <Section className="pt-8 pb-12">
        <nav className="mb-6 text-sm text-muted-foreground">
          <Link to="/bn" className="hover:text-foreground">হোম</Link>
          <span className="mx-2">/</span>
          <Link to="/bn/blog" className="hover:text-foreground">ব্লগ</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground/80">{name}</span>
        </nav>

        <div className="text-xs uppercase tracking-wider text-primary mb-4">
          {copy?.tagline ?? name} · {posts.length} টি আর্টিকেল
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight">
          {name} <span className="text-gradient">আর্টিকেল</span>
        </h1>
        {copy?.intro && (
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
            {copy.intro}
          </p>
        )}
      </Section>

      <Section className="pb-16">
        {posts.length === 0 ? (
          <p className="text-muted-foreground">
            এই ক্যাটাগরিতে এখনো কোনো আর্টিকেল নেই।{" "}
            <Link to="/bn/blog" className="text-primary underline-offset-4 hover:underline">
              সব আর্টিকেল দেখুন
            </Link>
            ।
          </p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2">
            {posts.map((p) => (
              <Link
                key={p.slug}
                to="/bn/blog/$slug"
                params={{ slug: p.slug }}
                className="group rounded-2xl border border-border bg-card p-6 transition hover:border-primary/40"
              >
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  {p.category} · {p.readMin} মিনিট পড়া
                </div>
                <h2 className="mt-3 font-display text-xl leading-snug group-hover:text-primary transition">
                  {p.title}
                </h2>
                <p className="mt-3 text-sm text-muted-foreground line-clamp-3">
                  {p.excerpt}
                </p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm text-primary">
                  পড়ুন <ArrowUpRight className="h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>

      {otherCategories.length > 0 && (
        <Section className="pb-24">
          <div className="rounded-3xl border border-border bg-card/40 p-8 lg:p-12">
            <h2 className="font-display text-2xl md:text-3xl">
              অন্যান্য <span className="text-gradient">ক্যাটাগরি</span>
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              বাংলা ব্লগের প্রতিটি আর্টিকেল একটি নির্দিষ্ট বিষয়ে ফাইল করা।
            </p>
            <ul className="mt-6 flex flex-wrap gap-2">
              {otherCategories.map((c) => (
                <li key={c.slug}>
                  <Link
                    to="/bn/blog/category/$category"
                    params={{ category: c.slug }}
                    className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 transition"
                  >
                    {c.name}
                    <span className="text-[10px] tabular-nums opacity-60">{c.count}</span>
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/bn/blog"
                  className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs hover:opacity-90 transition"
                >
                  সব আর্টিকেল
                  <ArrowUpRight className="h-3 w-3" />
                </Link>
              </li>
            </ul>
          </div>
        </Section>
      )}
    </>
  );
}